import { createContext, useContext, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useNav } from '../lib/nav'
import { I } from './Icons'
import { Page, TopBar, groupOf } from './Shell'
import { useStore } from '../state/store'
import { useCached } from '../lib/cache'
import { countWebNew } from '../lib/api'
import { readDay, type DayState } from '../state/day'
import { tabBarShown } from '../lib/device'
export { tabBarShown }

// Phone layout (iPhone 1, 29 Sep): white top bar with the faded wordmark and the name/lock pill on Home, a floating tab bar
// (Home, Sell, Trade, Intake, More) on the top-level screens, and More as the list of everything else. The iPad never renders
// any of this.

export const PhoneCtx = createContext<{ day: DayState; onLock: () => void }>({ day: { employee_id: '', name: '', date: '', openedAt: 0 }, onLock: () => {} })

const gridIcon = (s = 22) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" /></svg>
const lockIcon = (s = 14) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 11h12v10H6zM8 11V7a4 4 0 0 1 8 0v4" /></svg>

function useOwner() {
  const { employees } = useStore()
  const meId = readDay()?.employee_id ?? ''
  return employees.some((e) => e.employee_id === meId && e.rights === 'owner' && e.active)
}

export function useWebNew(enabled: boolean) {
  const W = useCached<number>('webNew', countWebNew, { staleMs: 30_000, enabled })
  return W.data ?? null
}

const TABS: { key: string; label: string; icon: (s?: number) => ReactNode; to: string }[] = [
  { key: 'home', label: 'Home', icon: I.home, to: '/' },
  { key: 'sell', label: 'Sell', icon: I.sell, to: '/sell' },
  { key: 'trade', label: 'Trade', icon: I.trade, to: '/trade' },
  { key: 'intake', label: 'Intake', icon: I.intake, to: '/intake' },
  { key: 'more', label: 'More', icon: gridIcon, to: '/more' },
]

export function TabBar() {
  const nav = useNav()
  const { pathname } = useLocation()
  const owner = useOwner()
  const webNew = useWebNew(owner)
  if (!tabBarShown(pathname)) return null
  const g = groupOf(pathname)
  const cur = ['home', 'sell', 'trade', 'intake'].includes(g) && pathname !== '/inbox' ? g : pathname === '/' ? 'home' : 'more'
  return (
    <nav className="tabbar" aria-label="Sections">
      {TABS.map((t) => (
        <button key={t.key} type="button" className="tabbar__item" aria-current={cur === t.key ? 'page' : undefined} onClick={() => nav(t.to)}>
          {t.icon(22)}<span>{t.label}</span>
          {t.key === 'more' && !!webNew && <span className="tabbar__badge" aria-label={`${webNew} new in the Inbox`}>{webNew}</span>}
        </button>
      ))}
    </nav>
  )
}

/** Home's bar on a phone: the wordmark and whoever is signed in, with the lock. */
export function PhoneHomeBar() {
  const { day, onLock } = useContext(PhoneCtx)
  const first = (day.name || '').trim().split(/\s+/)[0]
  return (
    <header className="pbar">
      <span className="wm pbar__wm" role="img" aria-label="Starchive">STARCHIVE</span>
      <button type="button" className="pbar__who" onClick={onLock} aria-label={`${first || 'Signed in'}, lock the app`}>
        {first || 'Lock'}<span className="pbar__lock">{lockIcon()}</span>
      </button>
    </header>
  )
}

export function More() {
  const nav = useNav()
  const { onLock } = useContext(PhoneCtx)
  const owner = useOwner()
  const webNew = useWebNew(owner)
  const rows: { t: string; s?: string; i: ReactNode; to: string; n?: number | null }[] = [
    { t: 'Listing QA', i: I.qa(22), to: '/qa' },
    { t: "Today's sales", i: I.sales(22), to: '/sales' },
    { t: 'Time clock', i: I.clock(22), to: '/time' },
    { t: 'Close out', i: I.close(22), to: '/close' },
    { t: 'Consignors', i: I.cons(22), to: '/consignors' },
    { t: 'Customers', i: I.team(22), to: '/customers' },
    { t: 'Pending intakes', i: I.intake(22), to: '/intake/pending' },
    { t: 'QA log', i: I.qa(22), to: '/qa/log' },
  ]
  const ownerRows: typeof rows = owner ? [
    { t: 'Inbox', s: 'Sell or consign and contact forms', i: I.inbox(22), to: '/inbox', n: webNew },
    { t: 'Team', s: 'Employee IDs and rights', i: I.team(22), to: '/team' },
  ] : []
  const Row = ({ r }: { r: (typeof rows)[number] }) => (
    <button type="button" className="row row--nav more__row" onClick={() => nav(r.to)}>
      <span className="more__icon">{r.i}</span>
      <span className="more__text"><span className="more__t">{r.t}</span>{r.s && <span className="more__s">{r.s}</span>}</span>
      {!!r.n && <span className="otile__count">{r.n}</span>}
      {I.chev()}
    </button>
  )
  return (
    <Page bar={<TopBar title="More" />}>
      <div className="more">
        <div className="card">{rows.map((r) => <Row key={r.t} r={r} />)}</div>
        {owner && (<><div className="home__label">Owners</div><div className="card">{ownerRows.map((r) => <Row key={r.t} r={r} />)}</div></>)}
        <button type="button" className="btn-secondary more__lock" onClick={onLock}>Lock the app</button>
      </div>
    </Page>
  )
}
